import React, { useMemo } from 'react';
import { Flex, Tooltip } from '@chakra-ui/react';
import { type SelectOption } from '@kula-ai/components';
import { InfoCircle } from '@kula-ai/icons-core';
import {
  useGetLocationAvailability,
  type MeetingLocations,
} from '@/api/useGetLocationAvailability';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { Hackerrank } from '@/assets/icons/ats';
import { Phone } from '@kula-ai/icons/outline/Phone';
import { Building07 } from '@kula-ai/icons/outline/Building07';
import { CodeBrowser } from '@kula-ai/icons/outline/CodeBrowser';

type LocationValue = keyof MeetingLocations;

const LOCATION_OPTIONS: {
  value: LocationValue;
  label: string;
  icon: React.ComponentType<any>;
}[] = [
  { value: 'zoom', label: 'Zoom', icon: Zoom },
  { value: 'google_meet', label: 'Google Meet', icon: Gmeet },
  { value: 'teams', label: 'Teams', icon: Teams },
  { value: 'phone_interview', label: 'Phone Interview', icon: Phone },
  { value: 'hackerrank', label: 'HackerRank', icon: Hackerrank },
  { value: 'codeinterview', label: 'Code Interview', icon: CodeBrowser },
  { value: 'onsite', label: 'Onsite / Physical', icon: Building07 },
];

interface UseLocationFieldConfigProps {
  name?: string;
  label?: string;
  condition?: Record<string, unknown>;
  isHackerRank?: boolean;
  isCodeInterview?: boolean;
  /**
   * True when the workspace has HR integration enabled but the current
   * user has no personal HR for Work account (BE returned
   * `hackerrank_account_not_connected`). Mirrors legacy
   * `noHackerrankAccount` — the HR option stays visible but is
   * disabled, with a tooltip explaining why.
   */
  hackerRankUserHasNoAccount?: boolean;
  /**
   * True when none of the candidate hosts (picked interviewers + current
   * user + edit-mode saved host) can host a Zoom meeting. Disables the
   * Zoom option with a tooltip so the recruiter doesn't pick it and land
   * on an empty Host dropdown.
   */
  zoomHasNoEligibleHost?: boolean;
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
}

const HACKERRANK_NO_ACCOUNT_TOOLTIP =
  'You need a HackerRank for Work account to perform this action';

const ZOOM_NO_ELIGIBLE_HOST_TOOLTIP =
  'None of the selected interviewers are part of your Zoom account, so ' +
  "they can't host the meeting.";

export function useLocationFieldConfig(props: UseLocationFieldConfigProps) {
  const {
    isHackerRank = false,
    isCodeInterview = false,
    hackerRankUserHasNoAccount = false,
    zoomHasNoEligibleHost = false,
  } = props;

  const { locations: availability } = useGetLocationAvailability();

  const options = useMemo(() => {
    // Don't show options until API has loaded — matches old behavior
    if (!availability) return [];

    return LOCATION_OPTIONS.filter((opt) =>
      Boolean(availability[opt.value])
    ).map((opt) => {
      const base = {
        value: opt.value,
        label: opt.label,
        leftElement: React.createElement(opt.icon, { size: 16 }),
      };
      // HackerRank-specific: disable when the workspace has the
      // integration but the current user doesn't have a personal HR
      // account. Mirrors legacy `SchLocation`'s `isDisabled:
      // !isHackerRankEnabled || noHackerrankAccount` rule (workspace-
      // disabled is covered by the availability filter above — the
      // option doesn't render at all in that case).
      if (opt.value === 'hackerrank' && hackerRankUserHasNoAccount) {
        return { ...base, disabled: true };
      }
      // Zoom-specific: disable when none of the candidate hosts can
      // host (all interviewers + recruiter are "Not part of Zoom").
      // Without this, the recruiter picks Zoom and lands on an empty
      // Host dropdown with nothing to choose.
      if (opt.value === 'zoom' && zoomHasNoEligibleHost) {
        return { ...base, disabled: true };
      }
      return base;
    });
  }, [availability, hackerRankUserHasNoAccount, zoomHasNoEligibleHost]);

  const defaultLocation = useMemo(() => {
    if (!options.length) return undefined;
    if (isHackerRank) return 'hackerrank';
    if (isCodeInterview) return 'codeinterview';
    const zoomOption = options.find((o) => o.value === 'zoom');
    if (zoomOption && !('disabled' in zoomOption && zoomOption.disabled)) {
      return 'zoom';
    }
    return options.find((o) => !('disabled' in o && o.disabled))?.value;
  }, [options, isHackerRank, isCodeInterview]);

  // Custom option renderer — only needed when we have a disabled HR
  // option to surface a hover tooltip on its info icon. For all other
  // options we replicate the kula-ai default look (icon + label) so
  // they look identical to the un-customised Select.
  //
  // Why we render manually instead of relying on `option.disabled +
  // rightElement`:
  //   - The Select's built-in disabled styling only changes label
  //     colour subtly (secondary-500 vs secondary-700). Recruiters
  //     reading the dropdown can't tell the row is disabled. Wrapping
  //     the icon + label in our own opacity-50 + cursor-not-allowed
  //     wrapper gives a clear visual cue.
  //   - For the hover bubble we use Chakra Popover wrapped in
  //     `<Portal>` (NOT Chakra Tooltip). Tooltip renders inline next
  //     to the trigger, so it gets clipped/stacked inside react-select's
  //     menu portal (`z-index: 9999999`) which has its own transform/
  //     overflow stacking context. Legacy KSelect (Chakra Menu-based)
  //     didn't have that container, so its plain Tooltip worked. With
  //     react-select we have to portal the bubble to `document.body`
  //     and bump z-index above the menu portal so it actually renders
  //     on top.
  const renderOption = useMemo(() => {
    if (!hackerRankUserHasNoAccount && !zoomHasNoEligibleHost) return undefined;
    const Renderer = (option: SelectOption) => {
      const disabledTooltip =
        option.value === 'hackerrank' && hackerRankUserHasNoAccount
          ? HACKERRANK_NO_ACCOUNT_TOOLTIP
          : option.value === 'zoom' && zoomHasNoEligibleHost
            ? ZOOM_NO_ELIGIBLE_HOST_TOOLTIP
            : null;
      const isDisabled = Boolean(disabledTooltip);
      const content = (
        <Flex
          flex="1"
          alignItems="center"
          gap="2"
          opacity={isDisabled ? 0.5 : 1}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
        >
          {option.leftElement}
          <span>{option.label}</span>
        </Flex>
      );
      if (!isDisabled) return content;
      return (
        <Flex alignItems="center" justifyContent="space-between" w="100%">
          {content}
          <Tooltip
            label={disabledTooltip}
            placement="top"
            hasArrow
            // Per-instance popper z-index override. In our chakra-ui
            // version (2.10.9) the `zIndex` prop and `motionProps.style`
            // both land on the INNER tooltip div, not the outer
            // positioner. The positioner's z-index comes from
            // `useStyleConfig("Tooltip", props)` which reads
            // `theme/components/ToolTip.js` (`zIndex: 999999`) — below
            // the kula-ai Select menu portal's `9999999`. The `sx` +
            // `[data-popper-placement]:has(>&)` selector walks UP from
            // the inner tooltip to its positioner ancestor and applies
            // z-index there. Pattern from
            // https://github.com/chakra-ui/chakra-ui/discussions/5238.
            sx={{
              '[data-popper-placement]:has(>&)': { zIndex: 99999999 },
            }}
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                pointerEvents: 'auto',
                color: 'var(--chakra-colors-pastel-1-dark)',
              }}
            >
              <InfoCircle size={16} />
            </span>
          </Tooltip>
        </Flex>
      );
    };
    return Renderer;
  }, [hackerRankUserHasNoAccount, zoomHasNoEligibleHost]);

  const fieldConfig = useMemo(
    () => ({
      type: 'select' as const,
      name: 'location',
      label: 'Location',
      options,
      condition: props.condition,
      disabled: isHackerRank || isCodeInterview,
      ...(props.config || {}),
      fieldProps: {
        ...(props.fieldProps || {}),
        ...(renderOption ? { renderOption } : {}),
      },
    }),
    [props, options, isHackerRank, isCodeInterview, renderOption]
  );

  return { fieldConfig, options, defaultLocation };
}
